import { featureToggles } from '../models/feature-toggles';
import { ActivatedRoute } from '@angular/router';
import {
  AuthEvent,
  AuthService,
  ConfigService,
  LuigiCoreService,
} from '@openmfp/portal-ui-lib';
import { filter, take } from 'rxjs';

export function initJoule(
  configService: ConfigService,
  authService: AuthService,
  activatedRoute: ActivatedRoute,
  luigiCoreService: LuigiCoreService,
) {
  return async (): Promise<void> => {
    authService.authEvents
      .pipe(
        filter((event) => event === AuthEvent.AUTH_SUCCESSFUL),
        take(1),
      )
      .subscribe({
        next: async () => {
          const featureTogglesFromQuery =
            activatedRoute.snapshot.queryParams.ft ?? '';
          const isJouleActive = featureTogglesFromQuery
            .split(',')
            .includes(featureToggles.ENABLE_JOULE);
          if (!isJouleActive) {
            return;
          }

          // Get configuration from portal context
          const portalConfig = await configService.getPortalConfig();
          const jouleBotName = portalConfig.portalContext.jouleBotName;
          const jouleDomainUrl = portalConfig.portalContext.jouleDomainUrl;

          const scriptElement = document.createElement('script');

          scriptElement.setAttribute('async', '');
          scriptElement.setAttribute(
            'src',
            `${jouleDomainUrl}/resources/public/webclient/bootstrap.js`,
          );
          // Joule client url https://hyperspace-joule.eu10.sapdas.cloud.sap/joule/hyperspace
          scriptElement.setAttribute('data-bot-name', jouleBotName);
          scriptElement.setAttribute('data-expander-type', 'CUSTOM');
          scriptElement.setAttribute(
            'data-expander-preferences',
            'JTdCJTIyb25ib2FyZGluZ01lc3NhZ2UlMjIlM0ElMjJDb21lJTIwc3BlYWslMjB0byUyMG1lJTIxJTIyJTJDJTIyZXhwYW5kZXJUaXRsZSUyMiUzQSUyMiUyMiUyQyUyMmV4cGFuZGVyTG9nbyUyMiUzQSUyMkNVWF9BdmF0YXIuc3ZnJTIyJTJDJTIydGhlbWUlMjIlM0ElMjJzYXBfaG9yaXpvbiUyMiU3RA=="',
          );

          setTimeout(() => {
            document.head.appendChild(scriptElement);

            const joule = new Joule(luigiCoreService);

            const existingSapDas = (window as any).sapdas || {};
            (window as any).sapdas = existingSapDas;
            const existingBridge = (window as any).sapdas.webclientBridge || {};
            (window as any).sapdas.webclientBridge = {
              ...existingBridge,
              onMessage: joule.handleMessage,
              getApplicationContext: joule.applicationContext,
            };
          }, 1000);
        },
      });
  };
}

class Joule {
  luigiCoreService: LuigiCoreService;

  constructor(luigiCoreService: LuigiCoreService) {
    this.luigiCoreService = luigiCoreService;
  }

  handleMessage = (jouleResponse: JouleResponse) => {
    if (jouleResponse.messages.length > 1) {
      const jouleAction: JouleAction = this.filterAction(
        jouleResponse.messages[0],
      );
      this.apply(jouleAction);
    }
  };

  applicationContext = () => {
    const globalCtx = this.luigiCoreService.getGlobalContext();

    const tenantId = globalCtx.portalContext.tenantId;

    const defaultIncidentUrl =
      'https://itsm.services.sap/sp?id=sc_cat_item&sys_id=703f22d51b3b441020c8fddacd4bcbe2&service_offering=e8a9578f1ba1ca9068e676628b4bcb3b&public=yes';
    const incidentUrl = globalCtx.helpContext.issueTracker
      ? globalCtx.helpContext.issueTracker.url
      : defaultIncidentUrl;

    const documentationUrl = globalCtx.helpContext.documentation.url;

    const pathSegments = new URL(document.URL).pathname.split('/');
    let projectId: string | undefined = undefined;
    if (pathSegments.includes('projects') && pathSegments.length > 2) {
      projectId = pathSegments[2];
    }

    return {
      app_title: 'Hyperspace',
      'hyperspace.tools.sap': {
        version: '0.1.0',
        tenantId: tenantId,
        ...(projectId && { projectId: projectId }),
        incidentUrl: incidentUrl,
        documentationUrl: documentationUrl,
      },
    };
  };

  private apply = (action: JouleAction) => {
    switch (action.action) {
      case 'create':
        if (action.semanticObject === 'project') {
          this.openCreateWorkspaceModal();
        }
        break;
      case 'dialog':
        if (action.semanticObject === 'help') {
          this.openHelpCenter();
        }
        break;
    }
  };

  private openCreateWorkspaceModal = () => {
    this.luigiCoreService.navigation().openAsModal('/create-workspace', {
      width: '630px',
      height: '700px',
      title: 'Create Workspace',
    });
  };

  private openHelpCenter = () => {
    this.luigiCoreService.navigation().openAsModal('/help', {
      size: 'fullscreen',
      _liveLabel: true,
      title: 'Help Center',
    });
  };

  private filterAction = (message: JouleMessage): JouleAction => {
    return message.attachment.content.elements.wizardManager;
  };
}

interface JouleResponse {
  messages: JouleMessage[];
}

interface JouleMessage {
  attachment: {
    content: {
      elements: {
        wizardManager: JouleAction;
      };
    };
  };
}

interface JouleAction {
  action: string;
  semanticObject: string;
}
